import {
  filterWithRelationsAndJson,
  type RelationalOrJsonEntityFilter,
} from "@shared/helpers/sql-relations";
import type { BaseRoles } from "@shared/Roles";
import { remult, type EntityFilter, type LifecycleEvent } from "remult";
import { doesExist } from "./does-exist";

/**
 * Represents a condition for allowing access to an entity.
 *
 * This can be:
 * - `boolean`: `true` to allow, `false` to deny. 🚦
 * - `(entity, event) => boolean`: A function that evaluates a condition based on the given entity. 🧠
 * - `(entity, event) => RelationalOrJsonEntityFilter<Entity> | EntityFilter<Entity>`: A function that returns a filter to be inject into an `exists` query
 *
 * @template Entity The entity type being checked.
 */
export type AllowRoleCheck<Entity> =
  | boolean
  | RelationalOrJsonEntityFilter<Entity>
  | EntityFilter<Entity>
  | ((
      entity: Entity,
      event: LifecycleEvent<Entity>,
    ) =>
      | boolean
      | Promise<boolean>
      | RelationalOrJsonEntityFilter<Entity>
      | EntityFilter<Entity>
      | Promise<RelationalOrJsonEntityFilter<Entity>>
      | Promise<EntityFilter<Entity>>);

/**
 * A callback function used within `allowPerRole` to define
 * entity-level access permissions based on user roles. 🛡️
 *
 * This function is overloaded to accept either a single role or an array of roles,
 * and a boolean or predicate function condition.
 *
 * @template Entity The entity type being checked.
 */
export interface AllowRole<Entity> {
  /**
   * Defines an allow condition for a single role.
   * @param role The role to allow access for.
   * @param condition A boolean or predicate function to determine access.
   */
  (role: BaseRoles, condition: AllowRoleCheck<Entity>): void;

  /**
   * Defines an allow condition for multiple roles.
   * If the user has *any* of these roles, the condition will be evaluated.
   * @param roles The roles to allow access for.
   * @param condition A boolean or predicate function to determine access.
   */
  (roles: BaseRoles[], condition: AllowRoleCheck<Entity>): void;
}

/**
 * A utility function to determine if the current user is allowed to access a given entity
 * based on their roles and conditions defined by the developer. 🧰
 *
 * This is useful for evaluating row-level or custom logic permissions in your application.
 *
 * @template Entity The entity type being checked.
 * @param allowed A callback where role-based conditions are defined using `allowRole`.
 * @param options Optional. Can override the current user's roles (useful for testing or simulation).
 * @returns A function that takes an entity and returns `true` if the user is allowed access, otherwise `false`.
 *
 * @example
 * ```ts
 * const canEdit = allowPerRole<Task>((allow) => {
 *   allow(Roles.Admin, true); // Admins always allowed
 *   allow(Roles.Editor, (task) => task.ownerId === remult.user?.id); // Editors can only edit their own
 * });
 *
 * const allowed = canEdit(myTask); // true or false
 * ```
 */
export function allowPerRole<Entity>(
  allowed: (allowRole: AllowRole<Entity>) => void,
  options: {
    userRoles?: BaseRoles[];
  } = {},
) {
  const { userRoles = remult.user?.roles ?? [] } = options;

  return async function (entity: Entity, event: LifecycleEvent<Entity>) {
    // 🚫 If no entity is provided, deny access
    if (!entity) {
      console.warn("No entity provided. Denying access to entity");
      return false;
    }

    // 🚫 If no roles are available, deny access
    if (userRoles.length === 0) {
      console.warn("No user roles found. Denying access to entity");
      return false;
    }

    const pendingTasks: (() => Promise<boolean | EntityFilter<Entity>>)[] = [];

    /**
     * Internal function for defining access conditions per role.
     *
     * If the user has any of the roles and the condition evaluates to `true`,
     * access is granted and further evaluations are skipped.
     */
    function allowRole(
      role: BaseRoles | BaseRoles[],
      condition: AllowRoleCheck<Entity>,
    ) {
      // ⏩ If already allowed, no need to check further
      if (isUserAllowed) return;

      const roles = Array.isArray(role) ? role : [role];

      const hasMatchingRole = userRoles.some((userRole) =>
        roles.includes(userRole as BaseRoles),
      );

      if (!hasMatchingRole) return;

      // 🧠 Evaluate condition (function or boolean)
      pendingTasks.push(async () => {
        if (typeof condition === "boolean") return condition;
        if (typeof condition === "object")
          return filterWithRelationsAndJson(event.metadata.entityType)(
            condition as RelationalOrJsonEntityFilter<Entity>,
          );
        const result = await condition(entity, event);
        if (typeof result === "boolean") return result;
        return filterWithRelationsAndJson(event.metadata.entityType)(
          result as RelationalOrJsonEntityFilter<Entity>,
        );
      });
    }
    // ✅ Flag to indicate if access has been granted
    let isUserAllowed = false;

    // 🛠 Execute developer-defined rules
    allowed(allowRole);

    const orFilters: EntityFilter<Entity>[] = [];

    for (const task of pendingTasks) {
      const filter = await task();
      if (typeof filter === "boolean" && filter) {
        isUserAllowed = true;
        break;
      } else if (typeof filter !== "boolean") {
        orFilters.push(filter);
      }
    }

    if (!isUserAllowed && orFilters.length > 0) {
      let idFilter: EntityFilter<Entity> = {};

      if (!event.isNew) {
        const entityId = event.metadata.idMetadata.getId(entity);
        idFilter = event.metadata.idMetadata.getIdFilter(entityId);
      }

      isUserAllowed = await doesExist<Entity>(event.metadata.entityType, {
        $and: [
          idFilter,
          {
            $or: orFilters,
          },
        ],
      } as EntityFilter<Entity>);
    }

    return isUserAllowed;
  };
}
